import {Component, OnDestroy, OnInit} from "@angular/core";
import {Subject} from "rxjs";
import {HttpErrorResponse, HttpStatusCode} from "@angular/common/http";
import {ActivatedRoute} from "@angular/router";
import {ApplicationDetailLog, SecureApplicationVerificationResponse} from "leia-api";
import {
  CODE_VERIFICATION_CANCEL_REDIRECT_URL, INVITATION_PATH,
  REVIEW_PATH,
  SECURE_SESSION_ID_URL_PARAM_NAME
} from "src/app/shared/constants";
import {FacadeService} from "src/app/shared/service-facade/facade.service";
import {CoreService} from "../../shared/services/core.service";
import {CodeVerificationService} from "../../shared/services/code-verification.service";
import {environment} from "../../../environments/environment";
import {ReviewService} from "src/app/review/services/review.service";
import {SectionStatusService} from "src/app/status-tracker/section-status.service";
import {SectionStates, Sections} from "src/app/status-tracker/model/section-status";
import {CancelDialogComponent} from "../../actions/dialogs/cancel-dialog/cancel-dialog.component";
import {MatDialog} from "@angular/material/dialog";
import {SettingsEnum} from "../../shared/model/settings.enum";
import {ApplicationChannel} from "../../shared/model/application-channel";
import {MainLayoutService} from "../../shared/services/main-layout.service";
import {ThemeService} from "../../core/services/theme.service";
import {ApplicationIdleService} from "src/app/core/services/application-idle.service";
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {SecureApplicationReasonEnum} from "../../shared/model/secure-application-reason.enum";
import { SignupService } from "../service/signup.service";

@Component({
  selector: 'app-code-verification',
  templateUrl: './code-verification.component.html',
  styleUrls: ['./code-verification.component.scss'],
})
export class CodeVerificationComponent implements OnInit, OnDestroy {
  title = "One time access code";
  description = "Please enter your one-time code to continue with your application.";
  error = "";
  secureSessionId: string | null = "";
  sessionId: string = "";
  destroySubject$: Subject<void> = new Subject();
  cancelURL: string = "";
  hideResendButton = true;
  loading = false;
  isInJointMaintenanceFlow = false;
  isInAuthenticatedFlow = false;
  invitationError = false;
  isBaskBrand = false;
  applicationChannelId = 0;

  routeLoading = true;

  imagePath = "";
  logoUrl = "";

  private _applicationDetailLog!: ApplicationDetailLog;
  private _brandId!: number;

  accessCodeForm: FormGroup = new FormGroup({
    accessCode: new FormControl('', {
      validators: [Validators.required, Validators.minLength(8), Validators.maxLength(8)]
    })
  })

  get accessCode() {
    return this.accessCodeForm.get('accessCode');
  }

  constructor(
    public dialog: MatDialog,
    public facade: FacadeService,
    private activatedRoute: ActivatedRoute,
    private coreService: CoreService,
    private codeVerificationServices: CodeVerificationService,
    private reviewService: ReviewService,
    private mainLayoutService: MainLayoutService,
    private themeService: ThemeService,
    private applicationIdleService: ApplicationIdleService,
    private statusTrackerService: SectionStatusService,
  ) {
  }

  ngOnInit(): void {
    this.isBaskBrand = this.facade.isBaskBrand();

    document.body.style.height = 'inherit';
    this.imagePath = "/assets/tcb-circle-star.svg";
    this.logoUrl = "/assets/tcb-logo-oneline-blue.svg";

    this.secureSessionId = this.activatedRoute
      .snapshot
      .paramMap
      .get(SECURE_SESSION_ID_URL_PARAM_NAME);

    //TODO Remove the below code when we permanently switch to the Alloy SDK.
    this.secureSessionId = this.secureSessionId?.split("&")[0]!;

    if (this.facade.isFromBranch()) {
      this.cancelURL = this.facade.getStorageItem(SettingsEnum.BranchCancelUrl);
      this.hideResendButton = false;
      this.routeLoading = false;
      return;
    } else {
      this.themeService.setBaseTheme();
    }

    this.mainLayoutService.setPageTitle("Access Your Application");

    this.facade.checkValidity(this.secureSessionId).toPromise().then(response => {
      // this.facade.getBrandSettings(environment.app_settings_version, response.channelId!),
      this.applicationChannelId = response.channelId!;
      this.applicationIdleService.initialize();
    }).catch((error) => {
      // catch error if invalid
      this.invitationError = true;
      this.routeLoading = false;
      throw error;
    }).then(async () => {
      const secureApplicationDetails = await this.facade.getSecureApplicationMetadata(this.secureSessionId!).toPromise();
      const applicationRouteId = secureApplicationDetails.applicationRouteId;
      const applicationSecureReasonId = secureApplicationDetails.applicationSecureReasonId;

      if (!this.applicationChannelId && !applicationRouteId && !applicationSecureReasonId) {
        this.invitationError = true;
        this.routeLoading = false;
        return;
      }

      this.facade.setSecureApplicationReason(applicationSecureReasonId as SecureApplicationReasonEnum);

      switch (applicationSecureReasonId) {
        case SecureApplicationReasonEnum.jointMaintenance:
          this.facade.isInJointMaintenanceFlow$.next(true);
          this.isInJointMaintenanceFlow = true;
          this.setUpJointMaintenanceFlow();
          break;
        case SecureApplicationReasonEnum.authenticated:
          this.facade.isInAuthenticatedFlow$.next([true, false]);
          this.isInAuthenticatedFlow = true;
          this.setUpAuthenticatedFlow();
          break;
        case SecureApplicationReasonEnum.resume:
          switch (this.applicationChannelId) {
            case ApplicationChannel.Authenticated:
              this.facade.isInAuthenticatedFlow$.next([true, true]);
              break;
            case ApplicationChannel.JointAppFromOlb:
            case ApplicationChannel.JointAppFromCsr:
              this.facade.isInJointMaintenanceFlow$.next(true);
              break;
          }
          this.routeLoading = false;
          break;
        default:
          this.routeLoading = false;
          break;

      }

    });
  }

  ngOnDestroy(): void {
    document.body.style.height = 'auto';
    this.destroySubject$.next();
  }

  verify(): void {
    if (this.accessCodeForm.valid) {
      this.loading = true;
      this.facade.verifyCode({
        secureSessionCode: this.accessCode?.value,
        secureSessionId: this.secureSessionId!
      }).subscribe(
        this.verificationSuccessful.bind(this),
        this.verificationUnsuccessful.bind(this)
      );
    }

  }

  cancel(): void {
    const dialogRef = this.dialog.open(CancelDialogComponent, {
      width: '400px'
    });
    dialogRef.afterClosed().subscribe(async userRequestsCancel => {
      if (!userRequestsCancel) return;
      window.location.href = this.cancelURL
    })
  }

  setUpAccountInfo(sessionId: string): any {
    return this.facade.attachExistingSession(sessionId)
      .then(() =>
        this.facade.getCatalogData().toPromise()
      ).then(async () => {
        this.reviewService.setApplicationData(
          this._applicationDetailLog,
          this.facade.getAnnualIncomes(),
          this.facade.getAllIncomeSources(),
          this.facade.getAllOccupations(),
          this.facade.getCountries(),
          this._brandId,
          this.isInJointMaintenanceFlow,
          this.isInAuthenticatedFlow
        )
      }).then(async () => await this.facade.updateApplication());
  }

  verificationSuccessful(response: SecureApplicationVerificationResponse): void {
    if (!response.verified) {
      this.verificationUnsuccessful(response);
      return;
    }

    if (this.facade.isFromBranch()) {
      this.coreService.navigate(`/${REVIEW_PATH}`);
      return;
    }

    this.loadPageSettings(response.sessionId!);
  }

  verificationUnsuccessful(response: SecureApplicationVerificationResponse | HttpErrorResponse): void {
    const httpError = (<HttpErrorResponse>response).error?.detail;
    if (!!httpError) {
      this.error = httpError;
      return;
    }

    const errorMessage = (<SecureApplicationVerificationResponse>response).message;
    if (!!errorMessage) {
      // this.clearInput();
      this.error = errorMessage;
    }
    this.loading = false;
  }

  onGenerateClick($event: MouseEvent) {
    this.codeVerificationServices.generateUniqueCode(this.secureSessionId!);
  }

  navigateTo(url: string): void {
    window.location.href = url;
  }

  private loadPageSettings(sessionId: string) {
    this.facade.setSessionId(sessionId);

    this.facade.getApplicationLog(sessionId).toPromise()
      .then(applicantDetailLog => {
        this._applicationDetailLog = applicantDetailLog;

        // Invitation will always initialy have one product
        const { productUID } = applicantDetailLog.products![0];
        this.facade.addStorageItem("productUid", productUID!);
        return this.facade.getProduct(productUID!);
      }).then(async product => {

      this.mainLayoutService.setPageTitle(`Access Your Application`);

      this.facade.addStorageItem("productId", product.productId.toString());
      this.facade.setBrandId(product.brandId.toString());
      this._brandId = product.brandId;

      this.facade.addStorageItem("olbGroupId", product.olbGroupId.toString());

      this.facade.addStorageItem("allowJointApplications", product.allowJointApplications.toString());
      this.facade.allowJointApplications$.next(product.allowJointApplications.toString() === "true");

      let alloyWorkflowId: string = "";
      if (product.alloyWorkflowId) alloyWorkflowId = product.alloyWorkflowId.toString();
      this.facade.addStorageItem("alloyWorkflowId", alloyWorkflowId);

      await this.facade.initializeProductStateUponVerification(product, this._applicationDetailLog);

      return Promise.all([
        this.facade.getBrandSettings(environment.app_settings_version, product.brandId),
        this.facade.getBrandSettings(environment.app_settings_version, product.brandId, product.productUID)
      ])
    }).then(settings => {
      const brandSettings = settings[0], productSettings = settings[1];

      brandSettings.forEach(setting => {
        if (setting.name !== undefined && setting.value !== undefined) {
          this.facade.addStorageItem(setting.name, setting.value);
        }
      });

      productSettings.forEach(setting => {
        if (setting.name !== undefined && setting.value !== undefined) {
          this.facade.addStorageItem(setting.name, setting.value);
        }
      });

      // Notify product and brand settings loaded
      this.facade.brandProductSettingsLoaded$.next(true);

      // Load the theme for the site.
      this.facade.setBrandTheme();
      this.cancelURL = this.facade.getStorageItem(CODE_VERIFICATION_CANCEL_REDIRECT_URL);
    }).then(() => {
      this.setUpAccountInfo(sessionId)
        .then(async () => {
          this.coreService.navigate(`/${REVIEW_PATH}`);
        });
    });
  }

  private setUpJointMaintenanceFlow() {
    this.statusTrackerService.removeStatusTrackerSection(Sections.SelectAccounts);
    this.statusTrackerService.updateStatusTrackerSectionStatus(Sections.YourApplication, SectionStates.notStarted);
    this.facade.getJointAccountSecureDetails(this.secureSessionId!).toPromise()
      .then(details => {
        this.facade.setJointApplicationDetails(JSON.stringify({
          "expiryDate": details.expiryDate,
          "invitedBy": details.invitedBy,
          "secureSessionId": this.secureSessionId
        }));
      })
      .then(() => this.coreService.navigate(`/${INVITATION_PATH}`));
  }

  private setUpAuthenticatedFlow() {
    this.statusTrackerService.removeStatusTrackerSection(Sections.CreateLogin);
    this.statusTrackerService.removeStatusTrackerSection(Sections.SelectAccounts);
    this.statusTrackerService.removeStatusTrackerSection(Sections.YourApplication);
    this.statusTrackerService.updateStatusTrackerSectionStatus(Sections.ReviewApplication, SectionStates.notStarted);
    this.facade.verifyCode({
      secureSessionId: this.secureSessionId!,
      acceptInvitation: true
    }).toPromise().then((response) => {
      if (!response.verified)
        throw HttpStatusCode.InternalServerError

      this.loadPageSettings(response.sessionId!);
    });
  }
}
